import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Sidebar } from '@/components/layout/Sidebar';
import { Header } from '@/components/layout/Header';
import { ModuleDisabled } from '@/components/layout/ModuleDisabled';
import { CapabilityMissing } from '@/components/layout/CapabilityMissing';
import { NotificationsContainer } from '@/components/ui/NotificationsContainer';
import { SessionGuard } from '@/components/session/SessionGuard';
import { UnsavedChangesBar } from '@/components/config/UnsavedChangesBar';
import { useGameStore } from '@/stores/game.store';
import { useAuthStore } from '@/stores/auth.store';
import { moduleOffReason, useProjectPagesStore } from '@/stores/projectPages.store';
import { usePanelFeaturesStore } from '@/stores/panelFeatures.store';
import { useActiveGroupCapabilities, useAllowedGameGroups, useGameGroupsStore } from '@/stores/gameGroups.store';
import { useNotificationsStore } from '@/stores/notifications.store';
import { apiService } from '@/services/api.service';
import { signOut } from '@/services/session';
import { staffUserFromInfo } from '@/devAuth';
import { getGameById, supportsGameGroups } from '@/config/games';
import { missingCapability, moduleForPath } from '@/config/panelModules';
import {
  effectiveGroup,
  fallbackMessage,
  gameGroupHeaderFor,
  groupViewOnly,
  membershipOutdated,
  noMemberGroup,
  normalizeGameGroupKey,
  planGroupSync,
  usesGameGroup,
  GAME_GROUP_PARAM,
  GAME_GROUP_REFUSED_EVENT,
} from '@/utils/gameGroups';
import styles from './AdminLayout.module.scss';

/** Attente maximale des groupes de jeu avant d'afficher la page avec le groupe persisté. */
const GROUPS_WAIT_MS = 4000;
/** Relecture des groupes (santé, cible ajoutée) au plus souvent : au retour sur l'onglet. */
const GROUPS_MAX_AGE_MS = 60_000;
/** Relecture forcée après un refus de groupe par la passerelle, au plus une fois par période. */
const GROUPS_REFUSED_THROTTLE_MS = 10_000;
/** Relecture de la session pendant un accès d'urgence, pour voir revenir la base des grades. */
const EMERGENCY_REFRESH_MS = 60_000;
/** Attente maximale des réglages des modules avant d'afficher la page d'un module quand même. */
const MODULES_WAIT_MS = 4000;

export function AdminLayout() {
  // Change de jeu => remonte les pages pour refetch les données du bon backend.
  const activeGameId = useGameStore((s) => s.activeGameId);

  // Menu mobile (sidebar en overlay sur petit écran)
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Session refusée par le serveur : bandeau explicite plutôt que des pages vides sans motif.
  const sessionRejected = useAuthStore((s) => s.sessionRejected);
  const clearSessionRejected = useAuthStore((s) => s.clearSessionRejected);

  // Accès d'urgence (base des grades injoignable, compte de PANEL_EMERGENCY_ADMINS) : bandeau
  // permanent. La session est relue à chaque chargement du layout, puis chaque minute tant que
  // l'accès d'urgence dure : au retour de la base, le bandeau et les droits temporaires tombent
  // seuls, et une panne survenue en cours de session est signalée au chargement suivant.
  const emergency = useAuthStore((s) => !!s.user?.emergency);
  const setUser = useAuthStore((s) => s.setUser);
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const res = await apiService.getCurrentUser();
      const current = useAuthStore.getState().user;
      if (cancelled || !res.data || !current) return;
      // La session n'est remplacée que si l'état d'urgence ou l'affectation aux groupes de jeu
      // change : hors de ces cas, rien de plus qu'avant.
      const groupsChanged = JSON.stringify(current.gameGroups ?? {}) !== JSON.stringify(res.data.gameGroups ?? {});
      if (!!current.emergency !== !!res.data.emergency || groupsChanged) setUser(staffUserFromInfo(res.data));
    };
    void refresh();
    if (!emergency) return () => { cancelled = true; };
    const timer = window.setInterval(() => void refresh(), EMERGENCY_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [emergency, setUser]);

  // Activation des pages de config par projet : chargee une fois, non persistee, pour qu'un
  // reglage change par un admin soit visible au rechargement suivant et pas des jours plus tard.
  const fetchProjectPages = useProjectPagesStore((s) => s.fetch);
  const fetchPanelFeatures = usePanelFeaturesStore((s) => s.fetch);
  useEffect(() => {
    void fetchProjectPages();
    void fetchPanelFeatures();
  }, [fetchProjectPages, fetchPanelFeatures]);

  // ---- Garde de route des modules ----
  // L'URL d'un module coupé pour le projet actif (global, projet, ou module parent) affiche un état
  // explicite au lieu de la page : favori, lien partagé, ou changement de projet sur la page. Les
  // pages hors module (Dashboard, Paramètres...) ne sont jamais concernées. Visibilité seulement :
  // les permissions de grade et la passerelle restent le contrôle d'accès.
  const moduleKey = moduleForPath(location.pathname);
  const modulePages = useProjectPagesStore((s) => s.pages);
  const modulePagesReady = useProjectPagesStore((s) => s.ready);
  const moduleCatalog = usePanelFeaturesStore((s) => s.features);
  const moduleCatalogReady = usePanelFeaturesStore((s) => s.loaded);
  const [modulesWaitExpired, setModulesWaitExpired] = useState(false);
  // Premier chargement seulement, et borné : sans ça, Mondes s'afficherait un instant sur un projet
  // où il est coupé, et lancerait ses appels avant d'être remplacé.
  const waitingModules = !!moduleKey && !(modulePagesReady && moduleCatalogReady) && !modulesWaitExpired;
  useEffect(() => {
    if (!waitingModules) return;
    const t = window.setTimeout(() => setModulesWaitExpired(true), MODULES_WAIT_MS);
    return () => window.clearTimeout(t);
  }, [waitingModules]);
  const moduleOff = moduleKey ? moduleOffReason(modulePages, moduleCatalog, activeGameId, moduleKey) : null;

  // ---- Groupes de jeu (NationsGlory : Nova, Iris...) ----
  // Tout ce bloc est inerte pour un jeu sans groupes (Paladium) : aucun appel, aucune attente,
  // aucune écriture d'URL, clé d'Outlet inchangée.
  const groupsSupported = supportsGameGroups(activeGameId);
  const groupsKnown = useGameStore((s) => !!s.gameGroupsKnown[s.activeGameId]);
  const persistedGroup = useGameStore((s) => s.activeGameGroupByGame[s.activeGameId] ?? null);
  const headerGroup = useGameStore((s) => gameGroupHeaderFor(s.activeGameId, s.gameGroupsKnown, s.activeGameGroupByGame));
  const setActiveGameGroup = useGameStore((s) => s.setActiveGameGroup);
  const groupEntry = useGameGroupsStore((s) => s.entries[activeGameId]);
  const groupsStatus = useGameGroupsStore((s) => s.status);
  const groupsError = useGameGroupsStore((s) => s.error);
  const fetchGameGroups = useGameGroupsStore((s) => s.fetch);
  // Affectation du membre du staff aux groupes (session) : null = tous. L'API marque déjà les autres.
  const allowedGroups = useAllowedGameGroups(activeGameId);
  const notifyWarning = useNotificationsStore((s) => s.warning);
  const groupPage = groupsSupported && usesGameGroup(location.pathname);

  // Affectation aux groupes changée pendant la session : les groupes (relus au retour sur l'onglet
  // ou après un refus de la passerelle) le montrent avant la session, qui est alors relue.
  const membershipStale = groupsSupported && membershipOutdated(groupEntry, allowedGroups);
  useEffect(() => {
    if (!membershipStale) return;
    let cancelled = false;
    void apiService.getCurrentUser().then((res) => {
      if (!cancelled && res.data && useAuthStore.getState().user) setUser(staffUserFromInfo(res.data));
    });
    return () => { cancelled = true; };
  }, [membershipStale, setUser]);
  const urlGroup = normalizeGameGroupKey(searchParams.get(GAME_GROUP_PARAM));

  // Lecture des groupes : seulement pour un jeu à groupes, relue au retour sur l'onglet si elle
  // date (santé des satellites, cible ajoutée par un admin).
  useEffect(() => {
    if (!groupsSupported) return;
    void fetchGameGroups({ maxAgeMs: GROUPS_MAX_AGE_MS });
    const onVisible = () => {
      if (document.visibilityState === 'visible') void fetchGameGroups({ maxAgeMs: GROUPS_MAX_AGE_MS });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [groupsSupported, fetchGameGroups]);

  // La passerelle a refusé le groupe envoyé (retiré du CICD, cible retirée...) : on relit les
  // groupes, et le repli ci-dessous choisit un groupe disponible avec une notification.
  useEffect(() => {
    if (!groupsSupported) return;
    let last = 0;
    const onRefused = () => {
      const now = Date.now();
      if (now - last < GROUPS_REFUSED_THROTTLE_MS) return;
      last = now;
      void fetchGameGroups({ force: true });
    };
    window.addEventListener(GAME_GROUP_REFUSED_EVENT, onRefused);
    return () => window.removeEventListener(GAME_GROUP_REFUSED_EVENT, onRefused);
  }, [groupsSupported, fetchGameGroups]);

  // `?g=` n'est lu qu'à l'ouverture (une fois par jeu) : ensuite, le sélecteur fait foi et l'URL le
  // suit. Sans ça, un retour arrière vers une page restée sur « g=nova » rebasculerait en silence.
  const urlReadFor = useRef<Set<string>>(new Set());
  // Dernière notification de repli : pas de doublon si l'effet rejoue (StrictMode, relecture).
  const lastFallbackNotice = useRef<{ message: string; at: number } | null>(null);
  const urlPending = groupPage && !urlReadFor.current.has(activeGameId);
  // Groupe effectif, sur toute page (un groupe retiré est remplacé même depuis Paramètres, où la
  // liste des grades protégés lit le jeu) ; l'URL n'est lue que sur une page qui lit le jeu.
  const choice = groupsSupported && groupEntry
    ? effectiveGroup(groupEntry, persistedGroup, urlPending ? urlGroup : null, allowedGroups)
    : null;

  // Choix du groupe (URL à l'ouverture, persisté, racine...), repli notifié, puis URL alignée sur le
  // groupe réellement envoyé. Décision pure dans planGroupSync (utils/gameGroups).
  useEffect(() => {
    if (!groupsSupported) return;
    const game = activeGameId;
    const plan = planGroupSync({
      groupPage,
      urlRead: urlReadFor.current.has(game),
      entry: groupEntry,
      status: groupsStatus,
      known: groupsKnown,
      persisted: persistedGroup,
      urlGroup,
      headerGroup,
      allowed: allowedGroups,
    });
    if (plan.markUrlRead) urlReadFor.current.add(game);
    if (plan.dropped) {
      const chosenKey = plan.setGroup !== undefined ? plan.setGroup : persistedGroup;
      const chosen = groupEntry?.groups.find((g) => g.key === chosenKey);
      const message = fallbackMessage(plan.dropped, chosen, getGameById(game).name);
      const last = lastFallbackNotice.current;
      if (!last || last.message !== message || Date.now() - last.at > 5000) {
        lastFallbackNotice.current = { message, at: Date.now() };
        notifyWarning(message, 8000);
      }
    }
    if (plan.setGroup !== undefined) {
      setActiveGameGroup(game, plan.setGroup);
      return; // l'URL suivra au rendu suivant
    }
    const writeUrl = plan.writeUrl;
    if (writeUrl) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(GAME_GROUP_PARAM, writeUrl);
        return next;
      }, { replace: true });
    }
  }, [groupsSupported, groupPage, activeGameId, groupEntry, groupsStatus, groupsKnown, persistedGroup, headerGroup,
      urlGroup, allowedGroups, setActiveGameGroup, setSearchParams, notifyWarning]);

  // En quittant un jeu à groupes pour un jeu sans groupes, `?g=` n'a plus de sens : retiré.
  const prevGameRef = useRef(activeGameId);
  useEffect(() => {
    const prev = prevGameRef.current;
    prevGameRef.current = activeGameId;
    if (prev === activeGameId || !supportsGameGroups(prev) || supportsGameGroups(activeGameId)) return;
    if (!searchParams.has(GAME_GROUP_PARAM)) return;
    setSearchParams((p) => {
      const next = new URLSearchParams(p);
      next.delete(GAME_GROUP_PARAM);
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGameId]);

  // Attente des groupes : seulement si ce jeu en avait au dernier chargement réussi et que la page
  // lit le jeu (Paladium n'attend jamais), bornée dans le temps (repli sur le groupe persisté).
  const [waitExpired, setWaitExpired] = useState(false);
  const waitingGroups = groupPage && groupsKnown && !groupEntry
    && (groupsStatus === 'idle' || groupsStatus === 'loading') && !waitExpired;
  useEffect(() => {
    if (!waitingGroups) return;
    const t = window.setTimeout(() => setWaitExpired(true), GROUPS_WAIT_MS);
    return () => window.clearTimeout(t);
  }, [waitingGroups]);
  // Groupe en cours de changement (URL à l'ouverture, repli) : on ne monte pas la page sur l'ancien.
  const resolvingGroup = groupPage && !!choice && choice.key !== persistedGroup;
  // Grades et accès ne lit pas le satellite (seule sa liste suit le groupe) : sans groupe
  // disponible, la page reste ouverte au lieu d'être remplacée par le message.
  const noGroupAvailable = groupPage && !groupViewOnly(location.pathname) && !!choice && choice.key === null;
  const groupsUnreadable = groupPage && groupsKnown && !groupEntry && groupsStatus === 'error';
  // Garde de capacité : page de Supervision que le satellite du groupe actif ne sert pas (groupe
  // « grades seuls » sans base : ni joueurs ni sanctions), cf config/panelModules. Capacités
  // inconnues (Paladium, groupes pas encore lus, satellite jamais sondé) : aucune garde. Grades et
  // accès ne lit pas le satellite et n'est jamais concerné.
  const activeCapabilities = useActiveGroupCapabilities();
  const lackingCapability = groupPage && !groupViewOnly(location.pathname)
    ? missingCapability(location.pathname, activeCapabilities)
    : null;

  // Ferme le menu à chaque changement de page
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // Applique l'accent du jeu actif (orange = Paladium, bleu = NationsGlory).
  // Nettoie au démontage pour revenir au violet Recube (ex: écran de login).
  useEffect(() => {
    const { accent } = getGameById(activeGameId);
    const root = document.documentElement;
    root.style.setProperty('--color-primary', accent.primary);
    root.style.setProperty('--color-primary-hover', accent.hover);
    root.style.setProperty('--color-primary-light', accent.light);
    return () => {
      root.style.removeProperty('--color-primary');
      root.style.removeProperty('--color-primary-hover');
      root.style.removeProperty('--color-primary-light');
    };
  }, [activeGameId]);

  // Clé de l'Outlet : un changement de jeu, ou de groupe sur une page qui lit le jeu, remonte les
  // pages, qui relisent leurs données sans rechargement complet. Hors page de groupe, la clé reste
  // celle du jeu : un repli de groupe ne remonte pas une page de config en cours d'édition. Grades et
  // accès ne relit rien au changement de groupe : sa liste suit le groupe sans remonter (recherche gardée).
  const outletKey = groupPage && !groupViewOnly(location.pathname)
    ? `${activeGameId}:${headerGroup ?? '-'}`
    : activeGameId;
  const gameName = getGameById(activeGameId).name;

  let content;
  if (waitingModules) {
    content = (
      <div className={styles.groupsLoading}>
        <Loader2 size={20} className={styles.spin} /> Chargement…
      </div>
    );
  } else if (moduleKey && moduleOff) {
    content = <ModuleDisabled moduleKey={moduleKey} off={moduleOff} />;
  } else if (waitingGroups || resolvingGroup) {
    content = (
      <div className={styles.groupsLoading}>
        <Loader2 size={20} className={styles.spin} /> Chargement des groupes de jeu…
      </div>
    );
  } else if (noGroupAvailable) {
    content = (
      <div className={styles.groupsLoading}>
        {noMemberGroup(groupEntry, allowedGroups)
          ? <>Tu n'es affecté à aucun groupe de {gameName} : demande l'accès à un responsable du panel.</>
          : <>Aucun groupe disponible pour {gameName} : déclare le satellite d'un groupe dans Paramètres &gt; Projets.</>}
      </div>
    );
  } else if (lackingCapability) {
    content = <CapabilityMissing />;
  } else {
    content = <Outlet key={outletKey} />;
  }

  return (
    <div className={styles.layout}>
      {/* Renouvelle la session pendant le travail ; fenêtre « session expirée » quand elle est finie. */}
      <SessionGuard />

      {sessionRejected && (
        <div className={styles.sessionBanner} role="alert">
          <span>
            Le serveur a refusé ta session (401) alors que ton jeton n'est pas expiré : plus aucune donnée
            ne remonte. Reconnecte-toi ; si ça persiste, c'est côté API.
          </span>
          <button
            type="button"
            onClick={() => { clearSessionRejected(); signOut(); }}
          >
            Se reconnecter
          </button>
        </div>
      )}

      <Sidebar open={navOpen} />

      {navOpen && <div className={styles.navOverlay} onClick={() => setNavOpen(false)} />}

      <div className={styles.main}>
        {emergency && (
          <div className={styles.emergencyBanner} role="alert">
            Accès d'urgence : la base des grades du panel est indisponible. Droits admin temporaires,
            certaines pages peuvent échouer.
          </div>
        )}

        <Header onMenuClick={() => setNavOpen((v) => !v)} />

        {groupsUnreadable && (
          <div className={styles.groupsBanner} role="status">
            <span>
              Groupes de jeu indisponibles{groupsError ? ` (${groupsError})` : ''} : les pages lisent
              {headerGroup ? <> <strong>{headerGroup}</strong>, le groupe choisi la dernière fois</> : ' le groupe racine'}.
              La passerelle refuse un groupe qui n'existe plus.
            </span>
            <button type="button" onClick={() => void fetchGameGroups({ force: true })}>Réessayer</button>
          </div>
        )}

        <main className={styles.content}>
          {content}
        </main>
      </div>

      <UnsavedChangesBar />
      <NotificationsContainer />
    </div>
  );
}
